export interface InputState {
  forward: number;  // -1, 0, or 1
  right: number;    // -1, 0, or 1
  mouseDeltaX: number;
  mouseDeltaY: number;
}

export interface KeyPressState {
  justPressed: Set<string>;
}

export class InputManager {
  private keys: Set<string> = new Set();
  private justPressedKeys: Set<string> = new Set();
  private mouseDeltaX = 0;
  private mouseDeltaY = 0;
  private isPointerLocked = false;

  readonly mouseSensitivity = 0.002;

  private canvas: HTMLCanvasElement;
  private instructions: HTMLElement | null;

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    this.instructions = document.getElementById('instructions');

    this.setupEventListeners();
  }

  private setupEventListeners(): void {
    // Keyboard events
    document.addEventListener('keydown', (e) => this.onKeyDown(e));
    document.addEventListener('keyup', (e) => this.onKeyUp(e));

    // Mouse events
    document.addEventListener('mousemove', (e) => this.onMouseMove(e));

    // Pointer lock
    this.canvas.addEventListener('click', () => this.requestPointerLock());
    document.addEventListener('pointerlockchange', () => this.onPointerLockChange());

    // Disable trackpad pinch-to-zoom
    document.addEventListener('wheel', (e) => {
      if (e.ctrlKey) {
        e.preventDefault();
      }
    }, { passive: false });

    // Disable Safari gesture zoom
    document.addEventListener('gesturestart', (e) => e.preventDefault());
    document.addEventListener('gesturechange', (e) => e.preventDefault());
    document.addEventListener('gestureend', (e) => e.preventDefault());
  }

  private onKeyDown(event: KeyboardEvent): void {
    if (!this.keys.has(event.code)) {
      this.justPressedKeys.add(event.code);
    }
    this.keys.add(event.code);
  }

  private onKeyUp(event: KeyboardEvent): void {
    this.keys.delete(event.code);
  }

  private onMouseMove(event: MouseEvent): void {
    if (this.isPointerLocked) {
      this.mouseDeltaX += event.movementX;
      this.mouseDeltaY += event.movementY;
    }
  }

  private requestPointerLock(): void {
    this.canvas.requestPointerLock();
  }

  private onPointerLockChange(): void {
    this.isPointerLocked = document.pointerLockElement === this.canvas;

    if (this.instructions) {
      if (this.isPointerLocked) {
        this.instructions.classList.add('hidden');
      } else {
        this.instructions.classList.remove('hidden');
      }
    }
  }

  getState(): InputState {
    // Calculate movement direction from keys
    let forward = 0;
    let right = 0;

    if (this.keys.has('KeyW') || this.keys.has('ArrowUp')) forward += 1;
    if (this.keys.has('KeyS') || this.keys.has('ArrowDown')) forward -= 1;
    if (this.keys.has('KeyA') || this.keys.has('ArrowLeft')) right -= 1;
    if (this.keys.has('KeyD') || this.keys.has('ArrowRight')) right += 1;

    // Get mouse delta and reset for next frame
    // Negate both: moving mouse right should turn right, moving up should look up
    const deltaX = -this.mouseDeltaX * this.mouseSensitivity;
    const deltaY = -this.mouseDeltaY * this.mouseSensitivity;
    this.mouseDeltaX = 0;
    this.mouseDeltaY = 0;

    return {
      forward,
      right,
      mouseDeltaX: deltaX,
      mouseDeltaY: deltaY
    };
  }

  isLocked(): boolean {
    return this.isPointerLocked;
  }

  wasKeyJustPressed(code: string): boolean {
    return this.justPressedKeys.has(code);
  }

  clearJustPressed(): void {
    this.justPressedKeys.clear();
  }
}
